import * as XLSX from "xlsx";

// XLSX export of the Fixture Matrix — 5 data sheets + legend, so users can
// paste the numbers anywhere (Sheets/Excel/Notion).

const LEGEND = [
    ["FPL TIMING ANALYZER — FIXTURE MATICE · EXPORT", ""],
    ["", ""],
    ["List", "Obsah"],
    ["Souhrn", "Jeden řádek na hráče: tým, pozice, cena, dlouhodobý PPG, součet očekávaných bodů za 38 GW a nejlepší/nejhorší soupeři podle historie."],
    ["GW body", "Očekávané body (expected PPG) hráče v každém GW1–GW38 nové sezóny 2026/27. Vyšší = lepší matchup."],
    ["GW odchylka", "Odchylka očekávaných bodů od dlouhodobého průměru hráče. Kladná (+) = nadprůměrná příležitost, záporná (−) = podprůměrný matchup."],
    ["GW soupeři", "Kdo je soupeř v daném GW. Formát: ZKRATKA (H) = doma, (A) = venku. Prázdné pole = blank GW (tým nehraje)."],
    ["Fáze", "Agregace po 6 fázích sezóny — průměrné očekávané PPG (Ø) a součet bodů (Σ) v každé fázi."],
    ["", ""],
    ["Pojem", "Vysvětlení"],
    ["Očekávané body (expected PPG)", "Blend tří signálů: historický výkon hráče v dané fázi sezóny × historický výkon proti danému soupeři × domácí/venkovní faktor."],
    ["Odchylka (deviation)", "Očekávané body v GW minus dlouhodobý průměr hráče (hist. PPG). Ukazuje, kdy je hráč 'nad plán' nebo 'pod plán'."],
    ["Hist. PPG", "Dlouhodobý průměr bodů na zápas ze všech dostupných historických sezón (2016/17–2025/26)."],
    ["Σ očekávané", "Součet očekávaných bodů přes všech 38 GW — hrubý odhad celosezónního výnosu při plné vytíženosti."],
    ["H / A", "H = domácí zápas (Home), A = venkovní zápas (Away)."],
    ["Blank GW", "Gameweek, ve kterém tým hráče nemá zápas — v exportu prázdná buňka."],
    ["Fáze sezóny", "GW1–6 (start), GW7–12, GW13–19, GW20–26, GW27–32, GW33–38 (finiš)."],
    ["Cena £—", "Hráč nováčka (nově povýšený tým) — FPL cena zatím nezveřejněna."],
    ["", ""],
    ["Zdroje dat", "Historie: Vaastav FPL dataset 2016/17–2025/26 · Rozpis 2026/27: SportMonks API."],
    ["Vygenerováno", new Date().toLocaleString("cs")],
];

const oppLabel = (c) => {
    if (c.blank) return "";
    const short = c.opp_short || (c.opponent ? c.opponent.slice(0, 3).toUpperCase() : "?");
    return `${short} (${c.is_home ? "H" : "A"})`;
};

const fmtOpps = (list) =>
    (list || []).map((o) => `${o.opponent} ${o.ppg} PPG (${o.games}z)`).join(" · ");

export function exportMatrixXlsx({ players, gws, phases }) {
    const wb = XLSX.utils.book_new();

    // --- Legend -----------------------------------------------------------
    const wsLegend = XLSX.utils.aoa_to_sheet(LEGEND);
    wsLegend["!cols"] = [{ wch: 34 }, { wch: 120 }];
    XLSX.utils.book_append_sheet(wb, wsLegend, "Vysvětlivky");

    // --- Summary ----------------------------------------------------------
    const summary = [[
        "Hráč", "Tým", "Pozice", "Cena (£m)", "Hist. PPG",
        "Σ očekávané (38 GW)", "Ø / GW", "Nejlepší soupeři (hist.)", "Nejhorší soupeři (hist.)",
    ]];
    players.forEach((p) => {
        summary.push([
            p.display_name, p.team || "", p.position || "",
            p.current_price ?? "—",
            p.overall_ppg, p.total_expected,
            Math.round((p.total_expected / 38) * 100) / 100,
            fmtOpps(p.best_opponents), fmtOpps(p.worst_opponents),
        ]);
    });
    const wsSum = XLSX.utils.aoa_to_sheet(summary);
    wsSum["!cols"] = [{ wch: 24 }, { wch: 16 }, { wch: 7 }, { wch: 9 }, { wch: 9 }, { wch: 17 }, { wch: 8 }, { wch: 60 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsSum, "Souhrn");

    // --- Per-GW sheets ------------------------------------------------------
    const gwHeader = ["Hráč", "Tým", "Pozice", ...gws.map((g) => `GW${g}`)];
    const buildGwSheet = (getVal) => {
        const rows = [gwHeader];
        players.forEach((p) => {
            const byGw = new Map((p.cells || []).map((c) => [c.gw, c]));
            rows.push([
                p.display_name, p.team || "", p.position || "",
                ...gws.map((g) => {
                    const c = byGw.get(g);
                    return !c || c.blank ? "" : getVal(c);
                }),
            ]);
        });
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws["!cols"] = [{ wch: 24 }, { wch: 16 }, { wch: 7 }, ...gws.map(() => ({ wch: 8 }))];
        return ws;
    };
    XLSX.utils.book_append_sheet(wb, buildGwSheet((c) => c.expected_ppg), "GW body");
    XLSX.utils.book_append_sheet(wb, buildGwSheet((c) => c.deviation), "GW odchylka");
    XLSX.utils.book_append_sheet(wb, buildGwSheet(oppLabel), "GW soupeři");

    // --- Phases -------------------------------------------------------------
    const phHeader = ["Hráč", "Tým", "Pozice"];
    phases.forEach((ph) => phHeader.push(`${ph.label} Ø`, `${ph.label} Σ`));
    const phRows = [phHeader];
    players.forEach((p) => {
        const row = [p.display_name, p.team || "", p.position || ""];
        phases.forEach((ph) => {
            const v = p.phase_expected?.[ph.key];
            row.push(v?.avg ?? "", v?.total ?? "");
        });
        phRows.push(row);
    });
    const wsPh = XLSX.utils.aoa_to_sheet(phRows);
    wsPh["!cols"] = [{ wch: 24 }, { wch: 16 }, { wch: 7 }, ...phases.flatMap(() => [{ wch: 11 }, { wch: 11 }])];
    XLSX.utils.book_append_sheet(wb, wsPh, "Fáze");

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `fixture-matice-${stamp}.xlsx`);
}
